function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Global } from '@emotion/core';
import { withMedia, MediaQueryProvider } from 'react-media-query-hoc';
import { FullscreenIcon, CollapseIcon } from '@domain-group/fe-co-icon';
import { withEventBoundary, useSendEvent } from '@domain-group/fe-co-allhomes-analytics';
import Modal from './components/modal';
import Carousel from './components/carousel/carousel';
import logEventForComponent from './utils/log-event-for-component';
import Drawer from './components/drawer';
import Navigation from './components/navigation';
import * as models from './models';
import * as styles from './allhomes-listing-photo-carousel.style';
import useControls from './use-controls';
import useCarousel from './use-carousel';
import useScreenLayout from './use-screen-layout';
import useNavItems from './use-nav-items';
import { jsx as ___EmotionJSX } from "@emotion/core";
var logEvent = logEventForComponent('TOP');
var AllhomesListingPhotoCarousel = _ref => {
  var items = _ref.items,
    initialIndex = _ref.initialIndex,
    onClose = _ref.onClose,
    onSlideChange = _ref.onSlideChange,
    onSlideLeft = _ref.onSlideLeft,
    onSlideRight = _ref.onSlideRight,
    onSlideClick = _ref.onSlideClick,
    fullscreenOnOpen = _ref.fullscreenOnOpen;
  var _useCarousel = useCarousel({
      items,
      initialIndex,
      onSlideChange,
      onSlideLeft,
      onSlideRight
    }),
    selectedIndex = _useCarousel.selectedIndex,
    updateSelectedIndex = _useCarousel.updateSelectedIndex;
  var controls = useControls();
  var _useScreenLayout = useScreenLayout(),
    layout = _useScreenLayout.layout,
    fullScreen = _useScreenLayout.fullScreen;
  var mobileNavItems = useNavItems({
    items,
    updateSelectedIndex
  });
  var _useSendEvent = useSendEvent(),
    sendEvent = _useSendEvent.sendEvent;
  var currentSlide = items[selectedIndex];

  // Video and Virtual tour are interactive so need special handling so that the controls
  // don't get in the way
  var interactive = ['VIRTUAL_TOUR', 'VIDEO'].includes(currentSlide.type);

  // Always show the controls for interactive items
  var visible = interactive || controls.visible;
  var emitEvent = name => sendEvent(name, {
    slide: selectedIndex,
    url: currentSlide.imageSrc,
    type: currentSlide.type
  });

  // On first mount we'll fade the controls in
  useEffect(() => {
    controls.show();
    emitEvent('carousel-show');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (fullscreenOnOpen) {
      fullScreen.enter({
        auto: true
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  var moveLeft = event => {
    updateSelectedIndex(selectedIndex - 1);
    event.stopPropagation();
  };
  var moveRight = event => {
    updateSelectedIndex(selectedIndex + 1);
    event.stopPropagation();
  };
  var handleSlideClick = function handleSlideClick() {
    emitEvent('carousel-click');
    onSlideClick(...arguments);
  };
  var handleKeyPress = event => {
    switch (event.keyCode) {
      case 27:
        // escape
        onClose();
        break;
      case 37:
        // left arrow
        moveLeft(event);
        break;
      case 39:
        // right arrow
        moveRight(event);
        break;
      default: // do nothing
    }
  };
  useEffect(() => {
    window.addEventListener('keyup', handleKeyPress);
    return () => window.removeEventListener('keyup', handleKeyPress);
  });
  var modalNavButtons = [...(layout.thumbnails ? [] : mobileNavItems), ...(fullScreen.isActive() ? [{
    icon: ___EmotionJSX(CollapseIcon, null),
    onClick: logEvent('onClick', fullScreen.exit),
    title: 'Leave full screen'
  }] : []), ...(fullScreen.isEnabled() && !fullScreen.isActive() ? [{
    icon: ___EmotionJSX(FullscreenIcon, null),
    onClick: logEvent('onClick', fullScreen.enter),
    title: 'Full screen'
  }] : [])];
  var photoCount = "".concat(selectedIndex + 1, "/").concat(items.length);
  return ___EmotionJSX(Modal, {
    fullscreenHandle: fullScreen.handle,
    onClose: onClose,
    navItems: modalNavButtons,
    photoCount: !layout.thumbnails && photoCount
  }, ___EmotionJSX("div", _extends({
    css: styles.fullScreen
  }, controls.containerProps), ___EmotionJSX(Global, {
    styles: styles.background
  }), ___EmotionJSX(Carousel, {
    layout: layout,
    items: items,
    selectedIndex: selectedIndex,
    onChange: updateSelectedIndex,
    onClick: handleSlideClick
  }), layout.modalNavButtons && items.length > 1 && ___EmotionJSX(Navigation, {
    visible: visible,
    onLeft: moveLeft,
    onRight: moveRight
  }), layout.thumbnails && ___EmotionJSX(Drawer, {
    visible: visible,
    interactive: interactive,
    items: items,
    selectedIndex: selectedIndex,
    updateSelectedIndex: updateSelectedIndex
  })));
};
AllhomesListingPhotoCarousel.propTypes = _objectSpread({
  items: PropTypes.arrayOf(PropTypes.shape(models.item)),
  initialIndex: PropTypes.number,
  onClose: PropTypes.func,
  onSlideChange: PropTypes.func,
  onSlideLeft: PropTypes.func,
  onSlideRight: PropTypes.func,
  onSlideClick: PropTypes.func
}, MediaQueryProvider.childContextTypes);
AllhomesListingPhotoCarousel.defaultProps = {
  items: [],
  initialIndex: 0,
  onClose: () => {},
  onSlideChange: () => {},
  onSlideLeft: () => {},
  onSlideRight: () => {},
  onSlideClick: () => {}
};
export default withEventBoundary('gallery')(withMedia(AllhomesListingPhotoCarousel));
export { AllhomesListingPhotoCarousel as BaseAllhomesListingPhotoCarousel };